import { useTranslations } from '@vocab/react';
import {
  Box,
  IconSkills,
  Inline,
  Stack,
  Tag,
  Text,
  TooltipRenderer,
} from 'braid-design-system';
import type { FC } from 'react';

import translations from '../.vocab';
import type { JobApplicationCardProps } from '../JobApplicationCard';

type Quality = JobApplicationCardProps['matchedQualities']['qualities'][number];
interface MatchedQualitiesProps {
  candidateFullname: string;
  qualities: Quality[];
  jobApplicationId: string;
  limit: number;
}

export const MatchedQualities: FC<MatchedQualitiesProps> = ({
  candidateFullname,
  qualities,
  limit,
}) => (
  <Inline space="xxsmall" alignY="center">
    {qualities.slice(0, limit).map((quality) => (
      <MatchedQuality
        key={quality.id}
        skill={quality.displayLabel}
        isTopSkill={quality.isTop}
        candidateFullname={candidateFullname}
      />
    ))}
  </Inline>
);

interface MatchedQualityProps {
  skill: string;
  isTopSkill: boolean;
  candidateFullname: string;
}

const MatchedQuality: FC<MatchedQualityProps> = ({
  candidateFullname,
  isTopSkill,
  skill,
}) => {
  const { t } = useTranslations(translations);
  return (
    <TooltipRenderer
      id="matched-qualities-tooltip"
      tooltip={
        isTopSkill ? (
          <Stack space="small">
            <Text size="small">
              <IconSkills /> {t('Top skill')}
            </Text>
            <Text size="small">
              {t(
                "We found this top skill in candidateFullname's application. Applications shortlisted for jobs similar to yours often have this top skill.",
                {
                  candidateFullname,
                },
              )}
            </Text>
          </Stack>
        ) : (
          <Text size="small">
            {t(
              "We found this skill in candidateFullname's application that matches your job ad.",
              { candidateFullname },
            )}
          </Text>
        )
      }
    >
      {({ triggerProps }) => (
        <Box {...triggerProps}>
          <Tag icon={isTopSkill ? <IconSkills /> : undefined} size="small">
            {skill}
          </Tag>
        </Box>
      )}
    </TooltipRenderer>
  );
};
